import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useHesapAuth } from '../../store/hesapStore';
import { isHesapSubdomain } from '../../lib/subdomain';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { BrandLogo } from '../../components/hesap/BrandLogo';
import { useHesapBranding } from '../../store/hesapBrandingStore';
import { GoogleAuthButton, OrDivider, useAuthConfig } from '../../components/auth/GoogleAuthButton';
import { isNative } from '../../lib/mobile';
import { showLoginError } from '../../lib/authFlows';
import { Loader2, Mail, Lock, Eye, EyeOff, Shield, BarChart3, Receipt, Cloud } from 'lucide-react';

const GoogleOr = () => {
  const cfg = useAuthConfig('/api/hesap/auth');
  return cfg?.client_id && !isNative() ? <OrDivider dark /> : null;
};

const GoogleSection = ({ onCredential }) => (
  <GoogleAuthButton apiBase="/api/hesap/auth" onCredential={onCredential} theme="filled_black" testId="hesap-google-login" />
);

export default function HesapLoginPage() {
  const { login, loginWithGoogle } = useHesapAuth();
  const navigate = useNavigate();
  // Email ve "beni hatırla" tercihleri localStorage'da saklanır (parola DEĞİL)
  const [email, setEmail] = useState(() => localStorage.getItem('hesap_remember_email') || '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('hesap_remember_me') !== 'false');
  const brandTitle = useHesapBranding(s => s.title);
  const registerPath = isHesapSubdomain() ? '/kayit' : '/hesap/kayit';
  const forgotPath = isHesapSubdomain() ? '/sifremi-unuttum' : '/hesap/sifremi-unuttum';

  const onGoogle = async (credential) => {
    try {
      await loginWithGoogle(credential);
      toast.success('Giriş başarılı');
      navigate(isHesapSubdomain() ? '/' : '/hesap');
    } catch (err) { toast.error(err.message, { duration: 8000 }); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      // Beni hatırla seçiliyse email'i kaydet, değilse temizle
      if (rememberMe) {
        localStorage.setItem('hesap_remember_email', email);
        localStorage.setItem('hesap_remember_me', 'true');
      } else {
        localStorage.removeItem('hesap_remember_email');
        localStorage.setItem('hesap_remember_me', 'false');
      }
      toast.success('Giriş başarılı');
      navigate(isHesapSubdomain() ? '/' : '/hesap');
    } catch (err) {
      showLoginError(err, '/api/hesap/auth', email);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex relative overflow-hidden">
      {/* Animated background gradients */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-emerald-500 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-sky-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-violet-600 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} />

      {isHesapSubdomain() && (
        <Link to="/" className="absolute top-5 left-5 z-20 text-xs text-slate-400 hover:text-white transition-colors" data-testid="login-home-link">← Ana sayfa</Link>
      )}

      {/* Left side: Brand & features (lg+) */}
      <div className="hidden lg:flex flex-col justify-center w-1/2 p-12 xl:p-20 relative z-10">
        <div className="max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <BrandLogo boxClass="w-14 h-14 rounded-2xl" iconClass="h-7 w-7" testId="login-brand-logo" />
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight">{brandTitle}</h1>
              <p className="text-emerald-400/80 text-xs font-medium tracking-wider uppercase">POS · Muhasebe · Yönetim</p>
            </div>
          </div>

          <h2 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
            İşletmenizi tek <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-sky-400">platformdan</span> yönetin.
          </h2>
          <p className="text-slate-400 text-lg leading-relaxed mb-10">
            Satıştan stok yönetimine, müşteri takibinden bulut depolamaya — her şey güvenli ve hızlı.
          </p>

          <div className="space-y-3">
            {[
              { Icon: Shield, text: 'Güvenli giriş, oturum otomatik kapanır' },
              { Icon: BarChart3, text: 'Gerçek zamanlı satış ve gelir/gider raporları' },
              { Icon: Receipt, text: 'POS sistemi + Veresiye yönetimi + PDF raporlar' },
              { Icon: Cloud, text: 'Müşteri dosyaları için bulut depolama & paylaşım' },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <f.Icon className="h-4 w-4 text-emerald-400" />
                </div>
                <span>{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side: Login card */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 relative z-10">
        <div className="w-full max-w-md">
          {/* Mobile-only logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex mb-4"><BrandLogo boxClass="w-16 h-16 rounded-2xl" iconClass="h-8 w-8" testId="login-mobile-brand-logo" /></div>
            <h1 className="text-3xl font-bold text-white">{brandTitle}</h1>
            <p className="text-emerald-400/80 text-xs font-medium tracking-wider uppercase mt-1">POS · Muhasebe Sistemi</p>
          </div>

          {/* Glassmorphism card */}
          <div className="backdrop-blur-xl bg-white/[0.03] border border-white/10 rounded-2xl p-7 sm:p-8 shadow-2xl">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white">Hoş geldiniz</h2>
              <p className="text-sm text-slate-400 mt-1">Hesabınıza giriş yapın</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="hesap-login-form">
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">E-POSTA</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 pointer-events-none" />
                  <Input type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ornek@firma.com" required
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-emerald-500 focus-visible:border-emerald-500/50 h-11"
                    data-testid="hesap-email-input" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">ŞİFRE</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 pointer-events-none" />
                  <Input type={showPw ? 'text' : 'password'} value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" required
                    className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-emerald-500 focus-visible:border-emerald-500/50 h-11"
                    data-testid="hesap-password-input" />
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition"
                    tabIndex={-1}>
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs">
                <label className="flex items-center gap-2 text-slate-400 cursor-pointer">
                  <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="rounded accent-emerald-500" data-testid="remember-me-checkbox" />
                  Beni hatırla
                </label>
                <Link to={forgotPath} className="text-emerald-400 hover:text-emerald-300 font-medium" data-testid="forgot-pw-link">
                  Şifremi unuttum
                </Link>
              </div>

              <Button type="submit" disabled={loading}
                className="w-full h-11 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold shadow-lg shadow-emerald-500/30 transition-all hover:shadow-emerald-500/50"
                data-testid="hesap-login-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Giriş Yap →'}
              </Button>
            </form>

            <GoogleOr />
            <GoogleSection onCredential={onGoogle} />

            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-sm text-slate-400">
                Hesabınız yok mu?{' '}
                <Link to={registerPath} className="text-emerald-400 hover:text-emerald-300 font-bold" data-testid="register-link">
                  Hemen Kayıt Olun →
                </Link>
              </p>
            </div>
          </div>

          <p className="text-center text-xs text-slate-600 mt-6">
            {brandTitle} © {new Date().getFullYear()} · Tüm hakları saklıdır
          </p>
        </div>
      </div>
    </div>
  );
}
